import { assertTransactionType, common, hasFlag, IOHelper, JSONHelper, MAX_TRANSACTION_ATTRIBUTES, } from '@neo-one/client-common';
import { utils as commonUtils } from '@neo-one/utils';
import _ from 'lodash';
import { AssetType } from '../AssetType';
import { MAX_TRANSACTION_SIZE } from '../constants';
import { VerifyError } from '../errors';
import { ScriptContainerType } from '../ScriptContainer';
import { BinaryReader, utils } from '../utils';
import { Witness } from '../Witness';
import { AttributeUsage, deserializeAttributeWireBase, UInt160Attribute } from './attribute';
import { hasDuplicateInputs, hasIntersectingInputs } from './common';
import { Input } from './Input';
import { Output } from './Output';
import { TransactionType } from './TransactionType';
const getUtilityValue = ({ outputs, utilityToken, }) => outputs
    .filter((output) => common.uInt256Equal(output.asset, utilityToken.hash))
    .reduce((acc, output) => acc.add(output.value), utils.ZERO);
export function TransactionBase(Base) {
    class TransactionBaseClass extends Base {
        constructor() {
            super(...arguments);
            this.equals = utils.equals(this.constructor, this, (other) => common.uInt256Equal(this.hash, other.hash));
            this.toKeyString = utils.toKeyString(TransactionBase, () => this.hashHex);
            this.getSortedScriptHashesForVerifying = utils.lazyAsync(async (options) => {
                const hashes = await this.getScriptHashesForVerifying(options);
                return [...hashes].sort();
            });
            this.sizeInternal = utils.lazy(() => IOHelper.sizeOfUInt8 +
                IOHelper.sizeOfArray(this.attributes, (attribute) => attribute.size) +
                IOHelper.sizeOfArray(this.inputs, (input) => input.size) +
                IOHelper.sizeOfArray(this.outputs, (output) => output.size) +
                IOHelper.sizeOfArray(this.scripts, (script) => script.size) +
                this.sizeExclusive());
            this.networkFee = utils.lazyAsync(async (context) => {
                const { getOutput, utilityToken } = context;
                const outputsForInputs = await Promise.all(this.inputs.map(getOutput));
                const inputValue = getUtilityValue({
                    outputs: outputsForInputs,
                    utilityToken,
                });
                const outputValue = getUtilityValue({
                    outputs: this.outputs,
                    utilityToken,
                });
                const result = inputValue.sub(outputValue).sub(this.getSystemFee(context));
                return result.lt(utils.ZERO) ? utils.ZERO : result;
            });
            this.getReferencesInternal = utils.lazyAsync(async ({ getOutput }) => Promise.all(this.inputs.map(async (input) => getOutput(input))));
            this.getTransactionResultsInternal = utils.lazyAsync(async ({ getOutput }) => {
                const inputOutputs = await this.getReferences({ getOutput });
                const mutableResults = {};
                const addOutputs = (outputs, negative) => {
                    outputs.forEach((output) => {
                        const key = common.uInt256ToHex(output.asset);
                        let result = mutableResults[key];
                        if (result === undefined) {
                            mutableResults[key] = result = utils.ZERO;
                        }
                        mutableResults[key] = result.add(negative === true ? output.value.neg() : output.value);
                    });
                };
                addOutputs(inputOutputs);
                addOutputs(this.outputs, true);
                return _.pickBy(mutableResults, (value) => value !== undefined && !value.eq(utils.ZERO));
            });
            this.baseGetScriptHashesForVerifyingInternal = utils.lazyAsync(async ({ getOutput, getAsset }) => {
                const [inputHashes, outputHashes] = await Promise.all([
                    Promise.all(this.inputs.map(async (input) => {
                        const output = await getOutput(input);
                        return common.uInt160ToHex(output.address);
                    })),
                    Promise.all(this.outputs.map(async (output) => {
                        const asset = await getAsset({ hash: output.asset });
                        if (hasFlag(asset.type, AssetType.DutyFlag)) {
                            return common.uInt160ToHex(output.address);
                        }
                        return undefined;
                    })).then((hashes) => hashes.filter(commonUtils.notNull)),
                ]);
                const attributeHashes = this.attributes
                    .map((attribute) => attribute instanceof UInt160Attribute && attribute.usage === AttributeUsage.Script
                    ? common.uInt160ToHex(attribute.value)
                    : undefined)
                    .filter(commonUtils.notNull);
                return new Set([...inputHashes, ...outputHashes, ...attributeHashes]);
            });
            this.sizeExclusive = () => 0;
        }
        static deserializeTransactionBaseStartWireBase({ reader, }) {
            const type = assertTransactionType(reader.readUInt8());
            const version = reader.readUInt8();
            return { type, version };
        }
        static deserializeTransactionBaseEndWireBase(options) {
            const { reader } = options;
            const attributes = reader.readArray(() => deserializeAttributeWireBase(options), MAX_TRANSACTION_ATTRIBUTES);
            const inputs = reader.readArray(() => Input.deserializeWireBase(options));
            const outputs = reader.readArray(() => Output.deserializeWireBase(options), utils.USHORT_MAX_NUMBER + 1);
            const scripts = reader.readArray(() => Witness.deserializeWireBase(options));
            return { attributes, inputs, outputs, scripts };
        }
        static deserializeWireBase(_options) {
            throw new Error('Not Implemented');
        }
        static deserializeWire(options) {
            return this.deserializeWireBase({
                context: options.context,
                reader: new BinaryReader(options.buffer),
            });
        }
        get size() {
            return this.sizeInternal();
        }
        async serializeTransactionBaseJSON(context) {
            const [networkFee, transactionData] = await Promise.all([
                this.getNetworkFee(context.feeContext),
                context.tryGetTransactionData(this),
            ]);
            return {
                txid: common.uInt256ToString(this.hashHex),
                size: this.size,
                version: this.version,
                attributes: this.attributes.map((attribute) => attribute.serializeJSON(context)),
                vin: this.inputs.map((input) => input.serializeJSON(context)),
                vout: this.outputs.map((output, index) => output.serializeJSON(context, index)),
                scripts: this.scripts.map((script) => script.serializeJSON(context)),
                sys_fee: JSONHelper.writeFixed8(this.getSystemFee(context.feeContext)),
                net_fee: JSONHelper.writeFixed8(networkFee),
                data: transactionData === undefined
                    ? undefined
                    : {
                        blockHash: common.uInt256ToString(transactionData.blockHash),
                        blockIndex: transactionData.startHeight,
                        transactionIndex: transactionData.index,
                        globalIndex: JSONHelper.writeUInt64(transactionData.globalIndex),
                    },
            };
        }
        async serializeJSON(_context) {
            throw new Error('Not Implemented');
        }
        async getNetworkFee(context) {
            return this.networkFee(context);
        }
        getSystemFee({ fees }) {
            const fee = fees[this.type];
            return fee === undefined ? utils.ZERO : fee;
        }
        async getReferences(options) {
            return this.getReferencesInternal(options);
        }
        async getTransactionResults(options) {
            return this.getTransactionResultsInternal(options);
        }
        async getScriptHashesForVerifying(options) {
            return this.baseGetScriptHashesForVerifyingInternal(options);
        }
        async verify(options) {
            if (this.size > MAX_TRANSACTION_SIZE) {
                throw new VerifyError('Transaction too large.');
            }
            const { memPool = [] } = options;
            if (hasDuplicateInputs(this.inputs)) {
                throw new VerifyError('Duplicate inputs');
            }
            if (memPool.some((tx) => !tx.equals(this) && hasIntersectingInputs(tx.inputs, this.inputs))) {
                throw new VerifyError('Input already exists in mempool');
            }
            if (this.attributes.filter((attribute) => attribute.usage === AttributeUsage.ECDH02 || attribute.usage === AttributeUsage.ECDH03).length > 1) {
                throw new VerifyError('Too many ECDH attributes.');
            }
            const [results] = await Promise.all([
                this.verifyScripts(options),
                this.verifyDoubleSpend(options),
                this.verifyOutputs(options),
                this.verifyTransactionResults(options),
            ]);
            return results;
        }
        async verifyDoubleSpend({ isSpent }) {
            const isDoubleSpend = await Promise.all(this.inputs.map(isSpent));
            if (isDoubleSpend.some((value) => value)) {
                throw new VerifyError('Transaction is a double spend');
            }
        }
        async verifyOutputs({ getAsset, currentHeight }) {
            const outputsGrouped = Object.entries(_.groupBy(this.outputs, (output) => common.uInt256ToHex(output.asset)));
            const hasInvalidOutputs = await Promise.all(outputsGrouped.map(async ([assetHex, outputs]) => {
                const asset = await getAsset({ hash: common.hexToUInt256(assetHex) });
                if (asset.expiration <= currentHeight + 1 &&
                    asset.type !== AssetType.GoverningToken &&
                    asset.type !== AssetType.UtilityToken) {
                    return true;
                }
                return outputs.some((output) => !output.value.mod(utils.TEN.pow(utils.EIGHT.subn(asset.precision))).eq(utils.ZERO));
            }));
            if (hasInvalidOutputs.some((value) => value)) {
                throw new VerifyError('Transaction has invalid output');
            }
        }
        async verifyTransactionResults({ getOutput, utilityToken, governingToken, fees, registerValidatorFee, }) {
            const results = await this.getTransactionResults({ getOutput });
            const resultsDestroy = Object.entries(results).filter(([_key, value]) => value.gt(utils.ZERO));
            if (resultsDestroy.length > 1 ||
                (resultsDestroy.length === 1 &&
                    !common.uInt256Equal(common.hexToUInt256(resultsDestroy[0][0]), utilityToken.hash))) {
                throw new VerifyError('Invalid destroyed output.');
            }
            const feeContext = {
                getOutput,
                governingToken,
                utilityToken,
                fees,
                registerValidatorFee,
            };
            const systemFee = this.getSystemFee(feeContext);
            if (systemFee.gt(utils.ZERO) && (resultsDestroy.length === 0 || resultsDestroy[0][1].lt(systemFee))) {
                throw new VerifyError('Not enough output value for system fee.');
            }
            const resultsIssue = Object.entries(results).filter(([__, value]) => value.lt(utils.ZERO));
            switch (this.type) {
                case TransactionType.Miner:
                case TransactionType.Claim:
                    if (resultsIssue.some(([assetHex]) => !common.uInt256Equal(common.hexToUInt256(assetHex), utilityToken.hash))) {
                        throw new VerifyError('Invalid miner/claim result');
                    }
                    break;
                case TransactionType.Issue:
                    if (resultsIssue.some(([assetHex, __]) => common.uInt256Equal(common.hexToUInt256(assetHex), utilityToken.hash))) {
                        throw new VerifyError('Invalid issue result');
                    }
                    break;
                default:
                    if (resultsIssue.length > 0) {
                        throw new VerifyError('Invalid results.');
                    }
            }
        }
        async verifyScripts({ getAsset, getOutput, verifyScript, }) {
            const hashesArr = await this.getSortedScriptHashesForVerifying({
                getAsset,
                getOutput,
            });
            if (hashesArr.length !== this.scripts.length) {
                throw new VerifyError(`Invalid witnesses. Found ${hashesArr.length} hashes and ${this.scripts.length} scripts.`);
            }
            const hashes = hashesArr.map((value) => common.hexToUInt160(value));
            return Promise.all(_.zip(hashes, this.scripts).map(async ([hash, witness]) => verifyScript({
                scriptContainer: {
                    type: ScriptContainerType.Transaction,
                    value: this,
                },
                hash: hash,
                witness: witness,
            })));
        }
    }
    TransactionBaseClass.WitnessConstructor = Witness;
    return TransactionBaseClass;
}

//# sourceMappingURL=TransactionBase.js.map
